import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	downloadRequestQuery,
	useAxiosInstance,
	useDownloadPoMutation,
} from '@gov-nx/api';
import type { GovError } from '@gov-nx/core/app';
import type { DownloadBlobSubscription } from '@gov-nx/core/events';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import { LocalizeNameSpaceTypes } from '@gov-nx/core/service';
import type { Optional } from '@gov-nx/core/types';
import { createUuid4 } from '@gov-nx/utils/common';

export const DownloadBlobListener = () => {
	const { t } = useTranslation(LocalizeNameSpaceTypes.Service);
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const [requestQueue, updateRequestQueue] = useState<
		DownloadBlobSubscription['payload'][]
	>([]);
	const [processingRequest, setProcessingRequest] =
		useState<Optional<DownloadBlobSubscription['payload']>>(undefined);

	const { download } = useDownloadEvents({
		onBlobDownload: (_, props) => {
			updateRequestQueue((queue) => [...queue, props]);
		},
	});

	const successMessage = (fileName: string) => {
		toastMessageSuccess(
			t('dokument.stazeni-dokumentu-uspech', {
				namespace: LocalizeNameSpaceTypes.Service,
				fileName,
			})
		);
	};

	const errorMessage = () => {
		toastMessageError(
			t('dokument.stazeni-dokumentu-chyba', {
				namespace: LocalizeNameSpaceTypes.Service,
			})
		);
	};
	const { axiosInstance } = useAxiosInstance();

	const mutation = useDownloadPoMutation<
		DownloadBlobSubscription['payload']['data']
	>((data) => downloadRequestQuery(axiosInstance, data), {
		onError: (error) => {
			errorMessage();
			processingRequest?.onError(error);
			setProcessingRequest(undefined);
		},
		onSuccess: async (data) =>
			download({
				blob: data.blob,
				id: createUuid4('blob'),
				antivirus: data.antivirus,
				fileName: data.fileName,
				callback: (id, success, error) => {
					if (success) {
						successMessage(data.fileName);
						processingRequest?.onSuccess();
					}
					if (error) {
						errorMessage();
						processingRequest?.onError(error as GovError);
					}
					setProcessingRequest(undefined);
				},
			}),
	});

	useEffect(() => {
		if (requestQueue.length > 0) {
			const [request, ...rest] = requestQueue;
			updateRequestQueue(rest);
			setProcessingRequest(request);
		}
	}, [requestQueue]);

	useEffect(() => {
		if (processingRequest) {
			mutation.mutate(processingRequest.data);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [processingRequest]);

	return null;
};
